import React, { useState, useEffect, useCallback } from 'react';
import {
  FaCloudUploadAlt,
  FaTimes,
  FaTrash,
  FaArrowLeft,
  FaSync,
  FaCalendarAlt,
  FaImage,
  FaFileImage,
  FaCheckCircle,
  FaSpinner,
  FaFileAlt,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getData, postData, deleteData } from '../../../Services/Fetchnodeservices';

const MenuUpdate = ({ restaurantId: propRestaurantId, onBack, isDark = false }) => {
  const navigate = useNavigate();
  
  // State
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restaurantId, setRestaurantId] = useState(null);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  // 🔥 FIX: Define serverURL and base URL for images
  const serverURL = 'https://new.zaptodoor.com';
  const BASE_URL = `${serverURL}/uploads/`;

  // 🔥 FIX: Function to get correct image URL
  const getImageUrl = useCallback((filename) => {
    if (!filename) return null;
    // If it's already a full URL, return as is
    if (filename.startsWith('http://') || filename.startsWith('https://')) {
      return filename;
    }
    // Remove any leading slashes
    const cleanFilename = filename.replace(/^\/+/, '');
    return `${BASE_URL}${cleanFilename}`;
  }, []);

  // Load restaurant ID
  useEffect(() => {
    const loadRestaurantId = async () => {
      try {
        let id = propRestaurantId || localStorage.getItem('restaurantId');
        if (!id) {
          const urlParams = new URLSearchParams(window.location.search);
          id = urlParams.get('restaurant_id') || urlParams.get('designation_id');
        }
        setRestaurantId(id);
        if (id) {
          fetchHistory(id);
        }
      } catch (error) {
        console.error('Load restaurant error:', error);
        alert('Failed to load restaurant data.');
      }
    };
    loadRestaurantId();
  }, [propRestaurantId]);

  // Fetch history
  const fetchHistory = useCallback(async (id) => {
    const restId = id || restaurantId;
    if (!restId) return;
    try {
      const response = await getData(`menuScanner/requests-by-restaurant/${restId}`);
      if (response.success) {
        setUploadHistory(response.data || []);
      }
    } catch (error) {
      console.error('History fetch error:', error);
    }
  }, [restaurantId]);

  // Refresh handler
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchHistory();
    setRefreshing(false);
  };

  // Pick images
  const pickImages = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = 'image/*';
    input.onchange = (e) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const newFiles = Array.from(files).map((file) => ({
          uri: URL.createObjectURL(file),
          name: file.name,
          type: file.type,
          isImage: true,
          file: file,
        }));
        setSelectedFiles((prev) => [...prev, ...newFiles]);
      }
    };
    input.click();
  };

  // Remove from selected
  const removeFile = (index) => {
    if (window.confirm('Remove this image from selection?')) {
      setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // Clear all selected files
  const clearAllFiles = () => {
    if (selectedFiles.length === 0) return;
    if (window.confirm('Remove all selected images?')) {
      setSelectedFiles([]);
    }
  };

  // Delete from history
  const deleteFileFromHistory = async (requestId, fileIndex, fileName) => {
    if (!window.confirm(`Delete "${fileName}" from history?`)) return;

    try {
      setDeleting(true);
      const response = await deleteData(`menuScanner/request/${requestId}/file/${fileIndex}`);
      setDeleting(false);
      if (response.success) {
        alert('File removed successfully.');
        fetchHistory();
      } else {
        alert(response.message || 'Failed to delete.');
      }
    } catch (error) {
      setDeleting(false);
      alert('Failed to delete file.');
    }
  };

  // Delete entire request from history
  const deleteRequest = async (requestId) => {
    if (!window.confirm('Delete this entire upload request?')) return;

    try {
      setDeleting(true);
      const response = await deleteData(`menuScanner/request/${requestId}`);
      setDeleting(false);
      if (response.success) {
        alert('Request deleted successfully.');
        fetchHistory();
      } else {
        alert(response.message || 'Failed to delete.');
      }
    } catch (error) {
      setDeleting(false);
      alert('Failed to delete request.');
    }
  };

  // Submit request
  const submitRequest = async () => {
    if (!restaurantId) {
      alert('Restaurant ID not found.');
      return;
    }
    if (selectedFiles.length === 0) {
      alert('Please select at least one image.');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();

      selectedFiles.forEach((file) => {
        formData.append('files', file.file);
      });
      formData.append('restaurant_id', restaurantId);

      const response = await postData('menuScanner/menu-update-request', formData);
      setLoading(false);

      if (response.success) {
        alert('Your Menu Update Request has been submitted successfully!');
        setSelectedFiles([]);
        fetchHistory();
      } else {
        alert(response.message || 'Submission failed.');
      }
    } catch (error) {
      setLoading(false);
      alert(error.message || 'Something went wrong.');
    }
  };

  // Open image preview
  const openPreview = (imageUrl) => {
    setPreviewImage(imageUrl);
  };

  // Close preview
  const closePreview = () => {
    setPreviewImage(null);
  };

  // Handle back navigation
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  // 🎨 ORANGE THEME CONFIGURATION (Matching ZaptoDoor Logo)
  const theme = {
    bg: isDark ? '#0f172a' : '#fff7ed',
    cardBg: isDark ? '#1e293b' : '#ffffff',
    text: isDark ? '#f8fafc' : '#0f172a',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    border: isDark ? 'rgba(249, 115, 22, 0.2)' : '#fed7aa',
    inputBg: isDark ? '#0f172a' : '#ffffff',
    hoverBg: isDark ? 'rgba(249, 115, 22, 0.1)' : '#ffedd5',
    primary: '#f97316',
    primaryLight: '#fb923c',
    primaryDark: '#ea580c',
    primaryGradient: isDark 
      ? 'linear-gradient(135deg, #ea580c 0%, #f97316 50%, #fb923c 100%)'
      : 'linear-gradient(135deg, #f97316 0%, #fb923c 50%, #fdba74 100%)',
    success: '#16a34a',
    danger: '#dc2626',
    shadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(249, 115, 22, 0.15)',
  };

  // Styles
  const styles = {
    container: {
      padding: '24px',
      maxWidth: '1200px',
      margin: '0 auto',
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      backgroundColor: theme.bg,
      minHeight: '100vh',
      transition: 'all 0.3s ease',
    },
    header: {
      background: theme.primaryGradient,
      borderRadius: '16px',
      padding: '20px 24px',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '16px',
      boxShadow: theme.shadow,
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.3)'}`,
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    },
    backButton: {
      padding: '8px 14px',
      background: 'rgba(255,255,255,0.2)',
      border: '1px solid rgba(255,255,255,0.3)',
      borderRadius: '10px',
      color: '#ffffff',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease',
      backdropFilter: 'blur(8px)',
    },
    headerTitle: {
      color: '#ffffff',
      fontSize: '22px',
      fontWeight: 800,
      margin: 0,
      letterSpacing: '-0.5px',
    },
    headerRight: {
      display: 'flex',
      gap: '10px',
    },
    refreshButton: {
      padding: '8px 14px',
      background: 'rgba(255,255,255,0.2)',
      border: '1px solid rgba(255,255,255,0.3)',
      borderRadius: '10px',
      color: '#ffffff',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      transition: 'all 0.2s ease',
      backdropFilter: 'blur(8px)',
    },
    uploadSection: {
      marginBottom: '24px',
    },
    uploadBox: {
      background: theme.primaryGradient,
      borderRadius: '16px',
      padding: '48px 24px',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      border: '2px dashed rgba(255,255,255,0.4)',
      boxShadow: theme.shadow,
    },
    uploadIcon: {
      fontSize: '48px',
      color: '#ffffff',
      marginBottom: '12px',
    },
    uploadText: {
      fontSize: '20px',
      fontWeight: 800,
      color: '#ffffff',
      marginBottom: '4px',
      letterSpacing: '-0.5px',
    },
    uploadSubText: {
      fontSize: '14px',
      color: 'rgba(255,255,255,0.85)',
      fontWeight: 500,
    },
    section: {
      marginBottom: '32px',
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
      flexWrap: 'wrap',
      gap: '8px',
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: 700,
      color: theme.text,
      margin: 0,
    },
    clearAllText: {
      color: theme.danger,
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      transition: 'opacity 0.2s',
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
      gap: '12px',
      marginBottom: '16px',
    },
    gridItem: {
      position: 'relative',
      aspectRatio: '1',
      borderRadius: '12px',
      overflow: 'hidden',
      backgroundColor: theme.hoverBg,
      border: `1px solid ${theme.border}`,
      transition: 'all 0.2s ease',
    },
    gridImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      cursor: 'pointer',
    },
    removeBadge: {
      position: 'absolute',
      top: '6px',
      right: '6px',
      background: theme.danger,
      border: 'none',
      borderRadius: '50%',
      width: '28px',
      height: '28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#ffffff',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 6px rgba(220, 38, 38, 0.3)',
    },
    submitButton: {
      width: '100%',
      padding: '14px',
      background: theme.primaryGradient,
      border: 'none',
      borderRadius: '12px',
      color: '#ffffff',
      fontSize: '16px',
      fontWeight: 700,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 16px rgba(249, 115, 22, 0.3)',
    },
    submitButtonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
      boxShadow: 'none',
    },
    historyList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    historyCard: {
      background: theme.cardBg,
      borderRadius: '12px',
      padding: '16px',
      border: `1px solid ${theme.border}`,
      transition: 'all 0.3s ease',
      boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.04)',
    },
    historyHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '12px',
      flexWrap: 'wrap',
    },
    historyDate: {
      fontSize: '13px',
      color: theme.textSecondary,
      fontWeight: 600,
      flex: 1,
    },
    historyCount: {
      fontSize: '12px',
      fontWeight: 700,
      color: theme.primaryDark,
      background: isDark ? 'rgba(249, 115, 22, 0.15)' : '#ffedd5',
      padding: '2px 12px',
      borderRadius: '12px',
      border: `1px solid ${isDark ? 'rgba(249, 115, 22, 0.3)' : '#fed7aa'}`,
    },
    historyImages: {
      display: 'flex',
      gap: '10px',
      overflowX: 'auto',
      paddingBottom: '4px',
    },
    historyImageWrapper: {
      position: 'relative',
      flexShrink: 0,
    },
    historyImg: {
      width: '80px',
      height: '80px',
      borderRadius: '8px',
      objectFit: 'cover',
      cursor: 'pointer',
      border: `1px solid ${theme.border}`,
      transition: 'transform 0.2s ease',
    },
    historyDeleteBtn: {
      position: 'absolute',
      top: '-6px',
      right: '-6px',
      background: isDark ? '#1e293b' : '#ffffff',
      border: `1px solid ${theme.border}`,
      borderRadius: '50%',
      width: '26px',
      height: '26px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.danger,
      cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      transition: 'all 0.2s ease',
    },
    historyDeleteRequest: {
      marginTop: '12px',
      padding: '8px 16px',
      background: isDark ? 'rgba(220, 38, 38, 0.1)' : '#fef2f2',
      border: `1px solid ${isDark ? 'rgba(220, 38, 38, 0.3)' : '#fecaca'}`,
      borderRadius: '8px',
      color: theme.danger,
      fontSize: '13px',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      transition: 'all 0.2s ease',
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
    },
    emptyIcon: {
      fontSize: '60px',
      color: theme.primary,
      opacity: 0.4,
      marginBottom: '16px',
    },
    emptyText: {
      fontSize: '18px',
      fontWeight: 700,
      color: theme.text,
      marginTop: '16px',
    },
    emptySubText: {
      fontSize: '14px',
      color: theme.textSecondary,
      marginTop: '8px',
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.85)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      cursor: 'pointer',
      backdropFilter: 'blur(4px)',
    },
    modalImage: {
      maxWidth: '90%',
      maxHeight: '90%',
      borderRadius: '12px',
      objectFit: 'contain',
      boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
    },
    modalClose: {
      position: 'absolute',
      top: '24px',
      right: '24px',
      background: 'rgba(255,255,255,0.15)',
      border: '1px solid rgba(255,255,255,0.2)',
      borderRadius: '50%',
      width: '48px',
      height: '48px',
      color: '#ffffff',
      fontSize: '24px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease',
      backdropFilter: 'blur(8px)',
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px',
      minHeight: '60vh',
    },
    spinner: {
      animation: 'spin 1s linear infinite',
    },
  };

  const spinnerStyles = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    .spin {
      animation: spin 1s linear infinite;
    }
    .upload-box-hover:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 40px rgba(249, 115, 22, 0.25);
    }
    .history-img-hover:hover {
      transform: scale(1.05);
    }
  `;

  // 🔥 FIX: Helper component to render history image with error handling
  const HistoryImage = ({ filename, onClick }) => {
    const [imgError, setImgError] = useState(false);
    const imageUrl = getImageUrl(filename);

    if (!imageUrl || imgError) {
      return (
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '8px',
          background: theme.hoverBg,
          border: `1px solid ${theme.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '4px',
          fontSize: '24px',
          color: theme.textSecondary,
        }}>
          <FaFileImage size={24} />
          <span style={{ fontSize: '8px', textAlign: 'center', padding: '0 4px' }}>
            {filename?.length > 15 ? filename.substring(0, 12) + '...' : filename}
          </span>
        </div>
      );
    }

    return (
      <img
        src={imageUrl}
        alt={filename}
        className="history-img-hover"
        style={styles.historyImg}
        onClick={onClick}
        onError={() => {
          console.error(`Failed to load image: ${imageUrl}`);
          setImgError(true);
        }}
      />
    );
  };

  if (!restaurantId) {
    return (
      <div style={styles.container}>
        <style>{spinnerStyles}</style>
        <div style={styles.loadingContainer}>
          <FaSpinner style={{ ...styles.spinner, fontSize: '40px', color: theme.primary }} />
          <p style={{ color: theme.textSecondary, marginTop: '12px', fontWeight: 500 }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{spinnerStyles}</style>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button 
            style={styles.backButton} 
            onClick={handleBack}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
          >
            <FaArrowLeft size={14} /> Back
          </button>
          <h1 style={styles.headerTitle}>Update Menu</h1>
        </div>
        <div style={styles.headerRight}>
          <button 
            style={styles.refreshButton} 
            onClick={handleRefresh}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
          >
            <FaSync className={refreshing ? 'spin' : ''} size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Upload Section */}
      <div style={styles.uploadSection}>
        <div 
          className="upload-box-hover"
          style={styles.uploadBox} 
          onClick={pickImages}
        >
          <FaCloudUploadAlt style={styles.uploadIcon} />
          <div style={styles.uploadText}>Add Menu Images</div>
          <div style={styles.uploadSubText}>Click to select photos (JPG, PNG)</div>
        </div>
      </div>

      {/* Selected Files */}
      {selectedFiles.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>Selected Images ({selectedFiles.length})</h3>
            <button 
              style={styles.clearAllText} 
              onClick={clearAllFiles}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              Clear All
            </button>
          </div>

          <div style={styles.gridContainer}>
            {selectedFiles.map((file, index) => (
              <div key={index} style={styles.gridItem}>
                <img
                  src={file.uri}
                  alt={file.name}
                  className="history-img-hover"
                  style={styles.gridImage}
                  onClick={() => openPreview(file.uri)}
                />
                <button
                  style={styles.removeBadge}
                  onClick={() => removeFile(index)}
                  title="Remove"
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <FaTimes size={14} />
                </button>
              </div>
            ))}
          </div>

          <button
            className="upload-box-hover"
            style={{
              ...styles.submitButton,
              ...(loading ? styles.submitButtonDisabled : {}),
            }}
            onClick={submitRequest}
            disabled={loading}
          >
            {loading ? (
              <FaSpinner className="spin" size={20} />
            ) : (
              <>
                <FaCheckCircle size={20} />
                Submit for Review
              </>
            )}
          </button>
        </div>
      )}

      {/* History Section */}
      {uploadHistory.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>
              Upload History ({uploadHistory.length})
            </h3>
            <span style={{ color: theme.textSecondary, fontSize: '13px', fontWeight: 500 }}>
              {uploadHistory.reduce((acc, item) => acc + (item.files?.length || 0), 0)} total files
            </span>
          </div>

          <div style={styles.historyList}>
            {uploadHistory.map((item) => {
              const files = item.files || [];
              return (
                <div key={item.id} style={styles.historyCard}>
                  <div style={styles.historyHeader}>
                    <FaCalendarAlt color={theme.primary} size={14} />
                    <span style={styles.historyDate}>
                      {new Date(item.created_at).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                    <span style={styles.historyCount}>
                      {files.length} file{files.length > 1 ? 's' : ''}
                    </span>
                  </div>

                  <div style={styles.historyImages}>
                    {files.map((file, fIdx) => (
                      <div key={fIdx} style={styles.historyImageWrapper}>
                        <HistoryImage 
                          filename={file.name}
                          onClick={() => {
                            const imageUrl = getImageUrl(file.name);
                            if (imageUrl) openPreview(imageUrl);
                          }}
                        />
                        <button
                          style={styles.historyDeleteBtn}
                          onClick={() => deleteFileFromHistory(item.id, fIdx, file.name)}
                          disabled={deleting}
                          title="Delete file"
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    style={styles.historyDeleteRequest}
                    onClick={() => deleteRequest(item.id)}
                    disabled={deleting}
                    onMouseEnter={(e) => e.currentTarget.style.background = isDark ? 'rgba(220, 38, 38, 0.2)' : '#fee2e2'}
                    onMouseLeave={(e) => e.currentTarget.style.background = isDark ? 'rgba(220, 38, 38, 0.1)' : '#fef2f2'}
                  >
                    <FaTrash size={14} /> Delete All
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {uploadHistory.length === 0 && selectedFiles.length === 0 && (
        <div style={styles.emptyState}>
          <FaFileAlt style={styles.emptyIcon} />
          <h3 style={styles.emptyText}>No uploads yet</h3>
          <p style={styles.emptySubText}>Upload menu images to get started</p>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div style={styles.modalOverlay} onClick={closePreview}>
          <button 
            style={styles.modalClose} 
            onClick={closePreview}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          >
            <FaTimes />
          </button>
          <img src={previewImage} alt="Preview" style={styles.modalImage} />
        </div>
      )}
    </div>
  );
};

export default MenuUpdate;